import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Snackbar,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { apiClient } from '../services/api'

interface Application {
    id: string
    description: string
    businessName: string
    brokerName: string | null
    brokerEmail: string | null
    statusId: number
    statusName: string
    createdDate: string
    submittedDate: string | null
}

const getStatusColor = (statusId: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (statusId) {
        case 1: // Draft
            return 'default'
        case 2: // SentToMerchant
            return 'info'
        case 3: // InReview
            return 'warning'
        case 4: // Submitted
            return 'primary'
        case 5: // Rejected
            return 'error'
        case 6: // Cancelled
            return 'default'
        case 7: // Closed
            return 'success'
        default:
            return 'default'
    }
}

const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

export default function ApplicationsPage() {
    const navigate = useNavigate()
    const [applications, setApplications] = useState<Application[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [applicationToDelete, setApplicationToDelete] = useState<Application | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [toastOpen, setToastOpen] = useState(false)
    const [toastMessage, setToastMessage] = useState('')
    const previousApplicationIdsRef = useRef<Set<string>>(new Set())

    useEffect(() => {
        fetchApplications()
    }, [])

    useEffect(() => {
        const interval = setInterval(() => {
            fetchApplications(false, true)
        }, 10000)

        return () => clearInterval(interval)
    }, [])

    const fetchApplications = async (showLoading = true, isPolling = false) => {
        try {
            if (showLoading) {
                setLoading(true)
            }
            const data = await apiClient.getApplications()

            // Check for new applications if this is a polling request
            if (isPolling && previousApplicationIdsRef.current.size > 0) {
                const newApplications = data.filter(
                    (app: Application) => !previousApplicationIdsRef.current.has(app.id)
                )

                if (newApplications.length > 0) {
                    const message = newApplications.length === 1
                        ? `New application received: ${newApplications[0].businessName || 'Unknown'}`
                        : `${newApplications.length} new applications received`
                    setToastMessage(message)
                    setToastOpen(true)
                }
            }

            // Update previous IDs
            previousApplicationIdsRef.current = new Set(data.map((app: Application) => app.id))

            setApplications(data)
            setError(null)
        } catch (err) {
            setError('Failed to load applications')
            console.error(err)
        } finally {
            if (showLoading) {
                setLoading(false)
            }
        }
    }

    const handleDeleteClick = (event: React.MouseEvent, application: Application) => {
        event.stopPropagation()
        setApplicationToDelete(application)
        setDeleteDialogOpen(true)
    }

    const handleDeleteConfirm = async () => {
        if (!applicationToDelete) return

        try {
            setDeleting(true)
            await apiClient.deleteApplication(applicationToDelete.id)
            setDeleteDialogOpen(false)
            setApplicationToDelete(null)
            await fetchApplications()
        } catch (err) {
            setError('Failed to delete application')
            console.error(err)
        } finally {
            setDeleting(false)
        }
    }

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false)
        setApplicationToDelete(null)
    }

    const handleToastClose = () => {
        setToastOpen(false)
    }

    if (loading) {
        return (
            <Container maxWidth="lg">
                <CircularProgress />
            </Container>
        )
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">{error}</Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="xl">
            <Typography variant="h4" component="h1" gutterBottom>
                Applications
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Merchant Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Broker Name</TableCell>
                            <TableCell>Broker Email</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {applications.map(application => (
                            <TableRow key={application.id} hover>
                                <TableCell
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/applications/${application.id}`)}
                                >
                                    {application.businessName}
                                </TableCell>
                                <TableCell
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/applications/${application.id}`)}
                                >
                                    {application.description}
                                </TableCell>
                                <TableCell
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/applications/${application.id}`)}
                                >
                                    {application.brokerName || '-'}
                                </TableCell>
                                <TableCell
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/applications/${application.id}`)}
                                >
                                    {application.brokerEmail || '-'}
                                </TableCell>
                                <TableCell
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/applications/${application.id}`)}
                                >
                                    <Chip label={application.statusName} color={getStatusColor(application.statusId)} size="small" />
                                </TableCell>
                                <TableCell
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/applications/${application.id}`)}
                                >
                                    {formatDate(application.createdDate)}
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        color="error"
                                        size="small"
                                        onClick={event => handleDeleteClick(event, application)}
                                        title="Delete application"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the application for{' '}
                        <strong>{applicationToDelete?.businessName}</strong>? This will permanently delete the application and
                        all associated data including business, borrowers, and bank information.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toastOpen}
                autoHideDuration={6000}
                onClose={handleToastClose}
                message={toastMessage}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            />
        </Container>
    )
}
